import { ClientLoggingService } from '../../shared/client-logging.service';

/**
 * Utility functions for generating ICC (International Code Council) URLs.
 *
 * The ICC website expects URLs in the format:
 *   https://codes.iccsafe.org/content/{documentSlug}/{chapterSlug}#{sectionId}
 *
 * Where chapterSlug is a human-readable slug like "chapter-11-accessibility".
 *
 * Data can come from two sources with different formats:
 *
 * 1. **Legacy ICC API**: chapterSlug is already human-readable (e.g., "chapter-11-accessibility")
 * 2. **Code Connect API**: chapterSlug is an API chapter ID (e.g., "IBC2018P6_Ch11")
 *
 * These utilities detect the source format and generate proper URL slugs accordingly.
 */

/**
 * Check if a chapterSlug looks like a Code Connect API chapter ID.
 *
 * Code Connect IDs have patterns like:
 *   - "IBC2018P6_Ch11"
 *   - "CABC2022P4_Ch03"
 *   - "IRC2021P3_Pt01_Ch01"
 *
 * @param chapterSlug The chapter slug to check
 * @returns true if it looks like a Code Connect API ID
 */
export function isCodeConnectChapterId(chapterSlug: string): boolean {
  if (!chapterSlug) return false;
  // Code Connect IDs contain underscores and typically match patterns like:
  // - {BOOK}_Ch{NUM} (e.g., IBC2018P6_Ch11)
  // - {BOOK}_Pt{NUM}_Ch{NUM} (e.g., IRC2021P3_Pt01_Ch01)
  // - {BOOK}_App{LETTER} (e.g., IBC2018P6_AppA)
  return /^[A-Z]{2,4}\d{4}(?:P\d)?_/.test(chapterSlug);
}

/**
 * Check if a chapterSlug looks like a human-readable URL slug.
 *
 * Human-readable slugs have patterns like:
 *   - "chapter-11-accessibility"
 *   - "appendix-a-employee-qualifications"
 *   - "part-i-administrative"
 *
 * @param chapterSlug The chapter slug to check
 * @returns true if it looks like a human-readable slug
 */
export function isHumanReadableSlug(chapterSlug: string): boolean {
  if (!chapterSlug) return false;
  // Human-readable slugs are lowercase, hyphenated, and start with common prefixes
  return /^(chapter|appendix|part|section)-[a-z0-9-]+$/.test(chapterSlug);
}

/**
 * Generate a URL-friendly chapter slug from the chapter title.
 *
 * Handles various title formats from both Legacy and Code Connect APIs:
 *   - "Chapter 11: Accessibility" -> "chapter-11-accessibility"
 *   - "Chapter 3: Occupancy Classification and Use" -> "chapter-3-occupancy-classification-and-use"
 *   - "Appendix A: Employee Qualifications" -> "appendix-a-employee-qualifications"
 *   - "ACCESSIBILITY" (Code Connect raw title with ordinal) -> needs ordinal parameter
 *
 * @param chapterTitle The chapter title
 * @returns URL-friendly slug or empty string if pattern doesn't match
 */
export function generateUrlSlugFromTitle(chapterTitle: string): string {
  if (!chapterTitle) return '';

  // Pattern 1: "Chapter X: Title" (Code Connect formatted or legacy)
  const chapterMatch = chapterTitle.match(/^Chapter\s+(\d+):\s*(.*)$/i);
  if (chapterMatch) {
    const ordinal = chapterMatch[1];
    const title = kebabCase(chapterMatch[2]);
    return title ? `chapter-${ordinal}-${title}` : `chapter-${ordinal}`;
  }

  // Pattern 2: "Appendix X: Title"
  const appendixMatch = chapterTitle.match(/^Appendix\s+([A-Za-z]):\s*(.*)$/i);
  if (appendixMatch) {
    const ordinal = appendixMatch[1].toLowerCase();
    const title = kebabCase(appendixMatch[2]);
    return title ? `appendix-${ordinal}-${title}` : `appendix-${ordinal}`;
  }

  // Pattern 3: "Part X: Title" or "Part X — Title" (some books use parts)
  const partMatch = chapterTitle.match(/^Part\s+([IVX]+|[A-Za-z]|\d+)[:\s—-]+\s*(.*)$/i);
  if (partMatch) {
    const ordinal = partMatch[1].toLowerCase();
    const title = kebabCase(partMatch[2]);
    return title ? `part-${ordinal}-${title}` : `part-${ordinal}`;
  }

  // Fallback: return empty string if pattern doesn't match
  // The caller should fall back to using the original chapterSlug
  return '';
}

/**
 * Convert a string to kebab-case for URL slugs.
 * @param str The input string
 * @returns Kebab-cased string suitable for URLs
 */
function kebabCase(str: string): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Collapse multiple hyphens
    .replace(/^-|-$/g, ''); // Trim leading/trailing hyphens
}

/**
 * Get the URL slug for a chapter, with smart detection of data source.
 *
 * Detection logic:
 * 1. If chapterSlug is already human-readable (legacy API), use it directly
 * 2. If chapterSlug is a Code Connect ID, generate slug from title
 * 3. Fall back to chapterSlug if title generation fails
 *
 * @param chapterTitle The chapter title (e.g., "Chapter 11: Accessibility")
 * @param chapterSlug The chapterSlug from the API (may be human-readable or API ID)
 * @returns The URL-friendly slug to use in ICC URLs
 */
export function getChapterUrlSlug(chapterTitle: string | undefined, chapterSlug: string | undefined): string {
  try {
    const slug = chapterSlug || '';
    const title = chapterTitle || '';

    // Case 1: chapterSlug is already human-readable (legacy API data)
    // Use it directly - no need to regenerate
    if (isHumanReadableSlug(slug)) {
      return slug;
    }

    // Case 2: chapterSlug looks like a Code Connect API ID
    // Generate the slug from the title instead
    if (isCodeConnectChapterId(slug) || slug === '') {
      const generatedSlug = generateUrlSlugFromTitle(title);
      if (generatedSlug) {
        return generatedSlug;
      }
    }

    // Case 3: Fallback - return whatever we have
    // This handles edge cases where the slug format is unknown
    return slug;
  } catch (error) {
    // Defensive: never throw, always return something usable
    ClientLoggingService.log('WARN', '[icc-url-utils] Error generating URL slug:', error);
    return chapterSlug || '';
  }
}
